import { postUrl } from '@/apiCalls/fetch';
import {
    selectPartnerNumber,
    selectTermsAndConditionsLink,
    selectUseCarExpertCopy,
} from '@/context/display';
import { store } from '@/context/store';
import { CAREXPERT_COPY } from '@/copy/carexpertCopy';
import { DEFAULT_COPY } from '@/copy/defaultCopy';
import { formatTermsSms } from '@/copy/formatTermsSms';
import { TRUCKSTOP_PARTNER_NAME } from '@/partners/truckstop';
import {
    ELocalStorageVariables,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '@/utils/localStorage';

import {
    EVerificationResponseStatus,
    IVerifyCodeReturn,
} from '../verificationCommonTypes';

import {
    IVerifySmsApiResponse,
    IVerifySmsCodeBody,
} from './verifySmsCodeTypes';

export async function verifySmsCode(
    mobilePhone: string,
    code: string
): Promise<IVerifyCodeReturn> {
    // ***** Redux State *****
    const state = store.getState();
    const borrower = state.borrowerReducer;
    const firstName = state.borrowerReducer.personalDetails.firstName ?? '';
    // The same selector the consent checkbox opens, so the link in the SMS
    // and the link they tapped before agreeing cannot drift apart.
    const termsAndConditionsLink = selectTermsAndConditionsLink(state);
    const sellerName = state.borrowerReducer.seller.name;
    const sellerNameFromVehicle =
        store.getState().borrowerReducer.vehicle.source;

    // ***** Request *****
    const url = '/api/verify';

    /*
     * The wording comes from the partner's copy set, the link from its widget
     * settings. Read off the store rather than useCopy() because this is a
     * plain function, not a component — same selector, same two copy sets.
     *
     * Truckstop is the exception: it has no copy set of its own, so its
     * wording stays here until one exists.
     */
    const copy = selectUseCarExpertCopy(state) ? CAREXPERT_COPY : DEFAULT_COPY;

    let message = formatTermsSms(copy.fields.termsSmsMessage, {
        firstName,
        link: termsAndConditionsLink,
        phone: selectPartnerNumber(state),
    });

    if (sellerName === TRUCKSTOP_PARTNER_NAME) {
        message =
            `Welcome from Odin Finance! You can find a copy of the terms and conditions here \n\n` +
            termsAndConditionsLink;
    }
    const seller2Name = store.getState().borrowerReducer.seller.name;
    let partner: string | undefined = undefined;
    if (seller2Name === 'Truckstop') {
        partner = 'ODIN_FINANCE';
    } else {
        partner = sellerNameFromVehicle ? sellerNameFromVehicle : sellerName;
    }

    const country = state.displayReducer.partner.country || 'AU';
    const widgetId = state.displayReducer.partner.widgetId ?? null;
    const body: IVerifySmsCodeBody = {
        mobilePhone,
        code,
        borrower,
        firstName,
        termsAndConditionsMessage: message,
        termsAndConditionsAgreed: Boolean(
            state.displayReducer.agreedToTermsAndConditions
                ?.isDefaultPrivacyAgreed?.agreed
        ),
        partner,
        country,
        test: 'message', // todo: remove this
        widgetId,
    };
    const response = await postUrl<IVerifySmsApiResponse>(url, body);

    // ***** Use Response *****
    if (
        response.token &&
        response.status === EVerificationResponseStatus.SUCCESSFUL
    ) {
        setLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN, response.token);
    } else {
        removeLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN);
        removeLocalStorageItem(ELocalStorageVariables.FIRST_NAME);
    }

    // ***** Return *****
    return {
        status: response.status,
        created: response.created,
    };
}
